/**
 * The bid amount.
 *
 * A bid is a whole number of dollars, so the field is a number with the two
 * buttons that step it — and the figure itself is an odometer, so a dollar
 * added moves one column rather than redrawing the amount. The input under the
 * odometer is a real text field: the figure can still be typed, selected, and
 * pasted over.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { MinusIcon, PlusIcon } from './Fields';
import { Reels } from './Odometer';

/** How long a held button waits before it starts repeating, and how fast. */
const HOLD_DELAY_MS = 400;
const HOLD_INTERVAL_MS = 90;

export function AmountStepper({
  label,
  value,
  onChange,
  hint,
  error,
  min = 0,
  step = 1,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
  readonly min?: number;
  readonly step?: number;
}): ReactNode {
  const [focused, setFocused] = useState(false);
  const timers = useRef<{ delay?: number; repeat?: number }>({});
  const latest = useRef(value);
  latest.current = value;

  // A whole-dollar figure is the only thing the odometer can roll; anything
  // else is shown as the characters that were typed until it is one again.
  const digits = value.trim().replace(/,/g, '');
  const numeric = /^\d+$/.test(digits) ? Number(digits) : undefined;

  function stopHold(): void {
    if (timers.current.delay !== undefined) window.clearTimeout(timers.current.delay);
    if (timers.current.repeat !== undefined) window.clearInterval(timers.current.repeat);
    timers.current = {};
  }

  useEffect(() => stopHold, []);

  function bump(direction: number): void {
    const current = latest.current.trim().replace(/,/g, '');
    const at = /^\d+$/.test(current) ? Number(current) : min;
    const next = Math.max(min, at + direction * step);
    latest.current = String(next);
    onChange(latest.current);
  }

  function hold(direction: number): void {
    bump(direction);
    stopHold();
    timers.current.delay = window.setTimeout(() => {
      timers.current.repeat = window.setInterval(() => bump(direction), HOLD_INTERVAL_MS);
    }, HOLD_DELAY_MS);
  }

  const classes = ['amountctl'];
  if (focused) classes.push('is-focus');
  if (numeric === undefined) classes.push('is-raw');

  return (
    <div className="field amountfield">
      <label htmlFor="bid-amount">{label}</label>
      <div className={classes.join(' ')}>
        <span className="amt-prefix" aria-hidden="true">
          $
        </span>
        <span className="amt-slot">
          {numeric === undefined ? null : (
            <span className="amt-reels" aria-hidden="true">
              <Reels text={value} order={numeric} variant="field" />
            </span>
          )}
          <input
            id="bid-amount"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className="amt-input"
            value={value}
            aria-invalid={error !== undefined}
            onChange={(event) => onChange(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </span>
        <span className="amt-steps">
          <button
            type="button"
            className="stepbtn is-lg"
            aria-label="Lower the bid by a dollar"
            disabled={numeric !== undefined && numeric <= min}
            onPointerDown={() => hold(-1)}
            onPointerUp={stopHold}
            onPointerLeave={stopHold}
            onPointerCancel={stopHold}
          >
            <MinusIcon />
          </button>
          <button
            type="button"
            className="stepbtn is-lg is-add"
            aria-label="Raise the bid by a dollar"
            onPointerDown={() => hold(1)}
            onPointerUp={stopHold}
            onPointerLeave={stopHold}
            onPointerCancel={stopHold}
          >
            <PlusIcon />
          </button>
        </span>
      </div>
      {hint === undefined ? null : <span className="hint">{hint}</span>}
      {error === undefined ? null : <span className="error">{error}</span>}
    </div>
  );
}
